import React from 'react'
import {
    TextField,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    FormGroup,
} from '@mui/material'
import {
    selectionControlSx,
    selectionControlLabelProps,
} from '@/shared/ui/input'
import { BlockTypes, BlockFormState, ItemState } from '../../types'
import SelectList from '@/feature/admin/Templates/select/SelectList'
import DossierQuerySelect from '@/feature/ContentDossier/select/DossierQuerySelect'

interface GeneralTabProps {
    kedo: any
    state: BlockFormState
    handleChange: (
        event: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => void
    handleChangeItem: (key: keyof ItemState, val: any) => void
    fetchCustomViews: () => void
    fetchQueries: () => void
    isEntityRequired: boolean
}

type BlockTypeOption = { value: string; label: string }

const GeneralTab: React.FC<GeneralTabProps> = ({
    kedo,
    state,
    handleChange,
    handleChangeItem,
    fetchCustomViews,
    fetchQueries,
    isEntityRequired,
}) => {
    const defDossierValue = state.defDossiers.some(
        (defDossier: any) =>
            String(defDossier.id) === String(state.item.defDossier || '')
    )
        ? state.item.defDossier
        : ''

    const blockTypeOptions: BlockTypeOption[] = Object.entries(BlockTypes).map(
        ([key, value]) => ({
            value,
            label: kedo.t(key),
        })
    )

    const selectedBlockType =
        blockTypeOptions.find(
            (option) => option.value === (state.item.type || '')
        ) || null

    const selectedDefDossier =
        state.defDossiers.find(
            (defDossier: any) =>
                String(defDossier.id) === String(defDossierValue)
        ) || null

    return (
        <>
            <Autocomplete
                size="small"
                options={blockTypeOptions}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                }
                value={selectedBlockType}
                onChange={(_event, option) => {
                    handleChange({
                        target: {
                            name: 'type',
                            value: option ? option.value : '',
                        },
                    } as React.ChangeEvent<HTMLInputElement>)
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required
                        size="small"
                        fullWidth
                        label={kedo.t('Block Type')}
                        margin="normal"
                        placeholder={kedo.t('Choose an option')}
                        error={!!(state.errors && state.errors.type)}
                        helperText={state.errors && state.errors.type}
                    />
                )}
            />

            {isEntityRequired && (
                <Autocomplete
                    size="small"
                    options={state.defDossiers || []}
                    getOptionLabel={(option) =>
                        kedo.env().translateItem(option, 'defdossier') || ''
                    }
                    isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                    }
                    value={selectedDefDossier}
                    onChange={(_event, option) => {
                        handleChange({
                            target: {
                                name: 'defDossier',
                                value: option ? option.id : '',
                            },
                        } as React.ChangeEvent<HTMLInputElement>)
                        fetchCustomViews()
                        fetchQueries()
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required
                            size="small"
                            fullWidth
                            label={kedo.t('Entity')}
                            margin="normal"
                            placeholder={kedo.t('Choose an option')}
                            error={!!(state.errors && state.errors.defDossier)}
                            helperText={state.errors && state.errors.defDossier}
                        />
                    )}
                />
            )}

            <FormControlLabel
                {...selectionControlLabelProps}
                control={
                    <Checkbox
                        size="small"
                        name="dashboard"
                        checked={state.item.dashboard}
                        onChange={handleChange}
                        disabled={state.loading}
                        sx={(theme) => selectionControlSx(theme)}
                    />
                }
                label={kedo.t('Dashboard')}
                sx={{ ...selectionControlLabelProps.sx, mt: 1, mb: 2 }}
            />

            <FormGroup>
                <SelectList
                    currentValue={state.item.parent_id}
                    onClear={() => handleChangeItem('parent_id', null)}
                    searchName={'search'}
                    kedo={kedo}
                    urlParams={{
                        page: 0,
                        limit: 200,
                        type: 'block_container',
                        environment: kedo.env().getEnvironment().id,
                    }}
                    onDisplayName={(item: any) =>
                        kedo.env().translateItem(item, 'mypageblock')
                    }
                    url={kedo.api().getMyPageBlockEndpoint()}
                    onSelect={(item: any) =>
                        handleChangeItem('parent_id', item.id)
                    }
                />
            </FormGroup>

            <FormGroup>
                <DossierQuerySelect
                    kedo={kedo}
                    onChange={(newId: any) =>
                        handleChangeItem('dossierQuery', newId)
                    }
                    value={state.item.dossierQuery}
                    ddId={state.item.defDossier}
                />
            </FormGroup>
        </>
    )
}

export default GeneralTab
